import type { Paged, TaskDto, TaskStatus } from '@ukinfo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { Pagination } from '../components/Pagination';
import { ScopedEmptyState } from '../components/ScopedEmptyState';
import { scopeQuery, useScope } from '../lib/scope';
import { useAuth } from '../lib/auth';
import {
  TASK_PRIORITY_LABELS,
  TASK_STATUS_LABELS,
  priorityTone,
  relativeDays,
  statusTone,
} from '../lib/format';
import { PageHeader } from '../components/PageHeader';
import { TaskDialog } from '../components/TaskDialog';
import { TemplateDialog } from '../components/TemplateDialog';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Checkbox,
  ErrorBlock,
  Input,
  LoadingBlock,
  Select,
  cn,
  useToast,
} from '../components/ui';

const STATUS_FILTERS: { id: string; label: string; statuses?: TaskStatus[] }[] = [
  { id: 'open', label: 'Offen', statuses: ['OPEN', 'IN_PROGRESS', 'WAITING'] },
  { id: 'all', label: 'Alle' },
  { id: 'done', label: 'Erledigt', statuses: ['DONE'] },
];

export function TasksPage(): JSX.Element {
  const scope = useScope();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = useAuth();
  const mayCreate = can('tasks', 'create');
  const mayUpdate = can('tasks', 'update');
  const mayManage = can('tasks', 'manage');
  const [params, setParams] = useSearchParams();

  const [filter, setFilter] = useState('open');
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [editing, setEditing] = useState<TaskDto | null>(null);

  const mineOnly = params.get('mine') === '1';
  const overdueOnly = params.get('overdue') === '1';

  const statusFilter = STATUS_FILTERS.find((f) => f.id === filter);

  const query = {
    ...scopeQuery(scope),
    ...(statusFilter?.statuses ? { status: statusFilter.statuses } : {}),
    mineOnly,
    overdueOnly,
    ...(search.trim().length >= 2 ? { search: search.trim() } : {}),
  };

  const [page, setPage] = useState(1);
  // Bei geaenderten Filtern wieder auf Seite 1 - sonst zeigt die Liste
  // scheinbar nichts, weil man auf Seite 3 einer kurzen Trefferliste steht.
  useEffect(() => setPage(1), [JSON.stringify(query)]);

  const seite = useQuery({
    queryKey: ['tasks', query, page],
    queryFn: () => api.get<Paged<TaskDto>>('/api/tasks', { ...query, page }),
  });
  const { isLoading, error } = seite;
  const data = seite.data?.items;
  const taskGroups: { title: string | null; tasks: TaskDto[] }[] = [];
  for (const task of data ?? []) {
    let group = taskGroups.find((entry) => entry.title === task.groupTitle);
    if (!group) {
      group = { title: task.groupTitle, tasks: [] };
      taskGroups.push(group);
    }
    group.tasks.push(task);
  }

  const toggleDone = useMutation({
    mutationFn: ({ task, done }: { task: TaskDto; done: boolean }) =>
      api.patch<TaskDto>(`/api/tasks/${task.id}`, { status: done ? 'DONE' : 'OPEN' }),
    onSuccess: async (_result, { done }) => {
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.show(done ? 'Aufgabe erledigt' : 'Aufgabe wieder geöffnet');
    },
    onError: (err: unknown) => {
      toast.show(err instanceof Error ? err.message : 'Änderung fehlgeschlagen', 'error');
    },
  });

  const removeTask = useMutation({
    mutationFn: (task: TaskDto) => api.del(`/api/tasks/${task.id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.show('Aufgabe gelöscht');
    },
    onError: (err: unknown) => {
      toast.show(err instanceof Error ? err.message : 'Löschen fehlgeschlagen', 'error');
    },
  });

  const setParam = (key: string, value: boolean): void => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, '1');
    else next.delete(key);
    setParams(next, { replace: true });
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Aufgaben"
        description="Wer erledigt was bis wann"
        actions={
          <>
            {mayManage && (
              <Button variant="secondary" onClick={() => setTemplateOpen(true)}>Aufgaben aus Vorlage</Button>
            )}
            {mayCreate && (
              <Button onClick={() => { setEditing(null); setDialogOpen(true); }}>+ Aufgabe</Button>
            )}
          </>
        }
      />

      <Card>
        <CardBody className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-auto min-w-32"
            >
              {STATUS_FILTERS.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}
                </option>
              ))}
            </Select>
            <Input
              placeholder="Suchen …"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-auto min-w-40 flex-1"
            />
          </div>
          <div className="flex flex-wrap gap-4">
            <Checkbox
              label="Nur meine Aufgaben"
              checked={mineOnly}
              onChange={(v) => setParam('mine', v)}
            />
            <Checkbox
              label="Nur überfällige"
              checked={overdueOnly}
              onChange={(v) => setParam('overdue', v)}
            />
          </div>
        </CardBody>
      </Card>

      {isLoading && <LoadingBlock />}
      {error && <ErrorBlock error={error} />}

      {data && data.length === 0 && (
        <ScopedEmptyState
          title="Keine Aufgaben gefunden"
          description="Für die aktuelle Auswahl gibt es nichts zu tun."
          filtersActive={filter !== 'open' || search !== '' || mineOnly || overdueOnly}
          onResetFilters={() => {
            setFilter('open');
            setSearch('');
            const next = new URLSearchParams(params);
            next.delete('mine');
            next.delete('overdue');
            setParams(next, { replace: true });
          }}
          createAction={
            mayCreate ? (
              <Button
                onClick={() => {
                  setEditing(null);
                  setDialogOpen(true);
                }}
              >
                Erste Aufgabe anlegen
              </Button>
            ) : undefined
          }
        />
      )}

      {data && data.length > 0 && (
        <div className="space-y-5">
          {taskGroups.map((group) => (
            <section key={group.title ?? 'ohne-gruppe'} className="space-y-2">
              {group.title && (
                <h2 className="border-b border-slate-200 pb-1 text-base font-semibold text-slate-800">
                  {group.title}
                </h2>
              )}
              <ul className="space-y-2">
                {group.tasks.map((task) => (
                  <li key={task.id}>
              <Card className={cn(task.isOverdue && 'border-rose-200')}>
                <div className="flex items-start gap-3 p-3">
                  <input
                    type="checkbox"
                    aria-label={`${task.title} erledigt`}
                    checked={task.status === 'DONE'}
                    disabled={toggleDone.isPending || task.blockedBy > 0}
                    hidden={!task.canChangeStatus}
                    onChange={(e) => toggleDone.mutate({ task, done: e.target.checked })}
                    className="mt-1 h-5 w-5 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  />
                  <Link to={`/aufgaben/${task.id}`} className="min-w-0 flex-1">
                    <span
                      className={cn(
                        'block font-medium text-slate-900',
                        task.status === 'DONE' && 'text-slate-400 line-through',
                      )}
                    >
                      {task.title}
                    </span>
                    <span className="mt-1 flex flex-wrap items-center gap-1.5">
                      <Badge tone={statusTone(task.status)}>
                        {TASK_STATUS_LABELS[task.status]}
                      </Badge>
                      {/* Sonst bliebe unklar, warum eine Aufgabe unter "meine"
                          steht, obwohl niemand namentlich daran haengt. */}
                      {task.assignees.length === 0 && task.status !== 'DONE' && (
                        <Badge tone="warning">Nicht zugewiesen</Badge>
                      )}
                      {task.priority !== 'NORMAL' && (
                        <Badge tone={priorityTone(task.priority)}>
                          {TASK_PRIORITY_LABELS[task.priority]}
                        </Badge>
                      )}
                      {task.dueAt && (
                        <Badge tone={task.isOverdue ? 'danger' : 'neutral'}>
                          {relativeDays(task.dueAt)}
                        </Badge>
                      )}
                      {task.blockedBy > 0 && (
                        <Badge tone="warning" title="Wartet auf andere Aufgaben">
                          wartet auf {task.blockedBy}
                        </Badge>
                      )}
                      {task.checklist.length > 0 && (
                        <Badge tone="neutral">
                          {task.checklist.filter((c) => c.isDone).length}/{task.checklist.length}
                        </Badge>
                      )}
                    </span>
                    <span className="mt-1 block truncate text-xs text-slate-500">
                      {task.organisationUnits.map((u) => u.name).join(', ')}
                      {task.assignees.length > 0 &&
                        ` · ${task.assignees.map((a) => a.name).join(', ')}`}
                    </span>
                  </Link>
                  {(task.canUpdate || task.canDelete) && (
                    <div className="flex flex-wrap gap-1">
                      {task.canUpdate && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditing(task);
                            setDialogOpen(true);
                          }}
                        >
                          Bearbeiten
                        </Button>
                      )}
                      {task.canDelete && (
                        <Button
                          variant="ghost"
                          size="sm"
                          loading={removeTask.isPending && removeTask.variables?.id === task.id}
                          onClick={() => {
                            if (window.confirm(`Aufgabe "${task.title}" wirklich löschen?`)) {
                              removeTask.mutate(task);
                            }
                          }}
                        >
                          Löschen
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </Card>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      {seite.data && (
        <Pagination
          page={seite.data.page}
          pageSize={seite.data.pageSize}
          total={seite.data.total}
          onPageChange={setPage}
        />
      )}

      {mayCreate || mayUpdate ? <TaskDialog open={dialogOpen} onClose={() => setDialogOpen(false)} task={editing} /> : null}
      {mayManage ? <TemplateDialog open={templateOpen} onClose={() => setTemplateOpen(false)} /> : null}
    </div>
  );
}
